/**
 * `AW15` · Borde de LECTURA del PADRÓN contra `apps/admin-web`.
 *
 * `contracts/read-api` fija el puerto (`MasterReadApi.listMaster`), la forma de
 * la fila (`MasterListItem`, los OCHO campos de `ADR-0191`) y el acotamiento
 * (`MasterQuery`); este archivo fija el cableado HTTP: la ruta, los nombres de
 * los parámetros de query y la validación de lo que llega. Mismo precedente que
 * `conversaciones/borde-conversaciones.ts` y `envios/borde-elegibles.ts`: el
 * consumidor decide cómo pedir, y el acuerdo de cable se declara acá y en el
 * informe.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ① ⛔ LO QUE ENTRA DEL REPO SON SÓLO TIPOS — `guard` ① de la etapa
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * **No se importa `modules/**`.** Los imports de `contracts/**` son `import
 * type`; el alias `@contracts/*` vive en `tsconfig.json` y NO en
 * `vite.config.ts`, así que importar un VALOR de un contrato rompe el build.
 *
 * El atajo que hay que no tomar es `openReadApi(...)` derecho, «total está en el
 * mismo repo». Falla por dos lados y ninguno avisa temprano:
 *
 *   · `openReadApi` abre un pool contra Postgres y `paginaDeMaster` corre
 *     sentencias ⇒ sería una conexión a la base **desde el navegador**, y el
 *     error aparece en el navegador y no en la revisión.
 *   · **compila en el dev server de quien lo escribe**: `management-ui` es un
 *     artefacto ESTÁTICO en S3 + CloudFront, no un proceso, así que un import de
 *     `modules/**` anda mientras haya un bundler y **no existe en producción**,
 *     donde no hay Node que lo ejecute ni credencial de base que usar.
 *
 * Y esa capa es la que sostiene «la UI no guarda nada, ni credenciales ni
 * estado»: el día que esta pantalla abra una conexión propia, la única unidad
 * que ve el padrón de ~16.000 menores deja de ser `admin-web`.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ② ⛔⭐ NI UN CAMPO DE PERSONA DE MÁS — LA FILA SE ARMA CAMPO POR CAMPO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `ADR-0191` §Riesgos ⭐⭐ lo midió y lo dejó escrito: **la proyección del padrón
 * CONSERVA los nombres del dominio** —`nombre`, `telefono`, `lote` y los cuatro
 * flags se llaman igual que en `Participant`—, así que un `Participant` entero
 * satisface la fila ESTRUCTURALMENTE y el chequeo de propiedades excedentes de
 * TypeScript **no mira un valor que no es un literal**. Es la asimetría exacta
 * con `EligibleListItem`, que renombra a `name`/`phone` y por eso allá el
 * compilador ataja solo (`apps/admin-web/src/rutas.ts:1268-1271`).
 *
 * `contracts/read-api` puso su propia defensa —los cinco excluidos declarados
 * `?: never`— **pero eso protege el borde del servidor, no a esta pantalla**:
 * lo que llega por el cable es `unknown`, y ningún tipo mira un JSON.
 *
 * ⇒ **acá la fila se arma con un objeto literal, campo por campo, ocho
 * propiedades escritas a mano.** Nada de `...fila`, nada de un `omit()`, nada de
 * un helper por reflexión sobre una lista de nombres: las tres devuelven un
 * valor no literal, y una fila que llegara con un noveno campo —un `rut`, un
 * `arm`, un `optOutFechaHora`— entraría a la memoria de la pantalla y podría
 * terminar dibujada por un `Object.entries` de mañana. Es lo simétrico de
 * `rutas.ts` ⟦tres-columnas-y-ni-una-mas⟧ del lado del navegador, y **no es
 * redundante**: son dos unidades distintas, y el día que una afloje la otra
 * sigue siendo el límite.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ③ ACÁ NO SE FILTRA, NO SE PAGINA Y NO SE RECUENTA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Los cinco parámetros se PIDEN y `total` se usa TAL CUAL: es el total en la
 * BASE del conjunto consultado (`MasterResult.total`), después del brazo, de los
 * `lotes` si vinieron, del `telefono` si vino y del `nombre` si vino. Filtrar en
 * el navegador las filas de la página ya traída es la variante más silenciosa
 * del error **y encima miente**, porque buscaría sobre una página de un conjunto
 * de cientos.
 *
 * **El brazo no viaja**: no hay `?arm=` ni `?brazo=`, ni header, ni cuerpo. Lo
 * resuelve `admin-web` de la sesión (`ADR-0009` condición 5, `rutas.ts`
 * ⟦ni-arm-ni-actor-en-el-cable⟧). Lo mismo el `actor`.
 */

import type { MasterListItem, MasterQuery, MasterResult } from "@contracts/read-api/index.ts"
import type { ConfiguracionInstancia } from "../configuracion.ts"
import { mensajeDeError } from "../mensaje-de-error.ts"
import { raizDelBrazo } from "../brazo-del-cable.ts"

/**
 * El acotamiento que la pantalla puede emitir.
 *
 * ⛔ **No tiene `arm` y no puede tenerlo** — `MasterQuery.arm` existe del lado
 * del puerto y lo pone `admin-web` desde la sesión; acá no hay dónde escribirlo.
 *
 * `lotes` es un arreglo posiblemente VACÍO y eso es deliberado: vacío significa
 * «sin acotar por lote», o sea el padrón completo del brazo, y el parámetro
 * simplemente **no se manda**. El contrato declara `readonly [string, ...string[]] | null`
 * justamente porque una lista vacía no es un estado alcanzable río abajo; la
 * traducción «vacío ⇒ ausente» es trabajo de esta capa y se hace una sola vez.
 */
export type FiltroDelPadron = {
  readonly lotes: readonly string[]
  /** Igualdad exacta sobre la forma canónica. `null` = sin acotar por este campo. */
  readonly telefono: string | null
  /** Coincidencia insensible a caja y a tildes. `null` = sin acotar por este campo. */
  readonly nombre: string | null
}

/**
 * El orden pedido AL SERVIDOR, tal como lo escribe el cable de `R3-UI3`
 * (`docs/_reports/r3-planificacion/cable-R3-UI3.md`): `<columna>-<sentido>`, con
 * `columna` entre las SIETE ordenables —lote, nombre, teléfono y los cuatro
 * estados, `ADR-0399 ⑫`— y `sentido` `asc`/`desc`.
 *
 * ⛔ **La consola NO ordena**: este tipo sólo describe lo que se le pide al
 * servidor sobre la lista ENTERA. Ordenar la página que llegó en el navegador
 * daría un orden distinto del que el servidor aplica a la lista completa, sin
 * un solo error (`RT 5`).
 */
export type OrdenDelPadron = {
  /**
   * ⭐ `ADR-0399 ⑫` `[29/9/2026]` · **EL SERVIDOR ORDENA TAMBIÉN POR LOS CUATRO
   * ESTADOS.** `ColumnaDeOrden` (`contracts/participants/index.ts:3325`) es
   * `"lote" | "nombre" | "telefono" | FlagName`, y el parámetro `orden` acepta
   * `opt_out-desc`, etc.: los empates por nombre y teléfono los resuelve el
   * servidor. Antes se declaraban sólo las tres primeras y los cuatro estados
   * quedaban fuera del orden —un `guard` de la etapa que el criterio sí exige—.
   * ⛔ La consola sigue sin ordenar: esto sólo describe lo que se pide al
   * servidor sobre la lista ENTERA.
   */
  readonly columna: "lote" | "nombre" | "telefono" | "sin_interaccion" | "siguio_con_chatbot" | "pidio_humano" | "opt_out"
  readonly sentido: "asc" | "desc"
}

/** Una consulta completa: el filtro APLICADO más la página y el orden pedidos. */
export type ConsultaDelPadron = {
  readonly filtro: FiltroDelPadron
  readonly pagina: number
  readonly tamanoDePagina: number
  /**
   * `orden?:` es **obligatoriamente opcional**, no cosmético: `simulador/**`
   * (`R3-UI6`) construye consultas sin orden, y hacerlo obligatorio rompe su
   * compilación (`⛔ guard` de `R3-UI3`). Ausente ⇒ rige el default del servidor
   * (el lote creado más recientemente primero).
   *
   * ⛔ RASTRO · `[29/9/2026]` · ~~`OrdenDelPadron`~~ → **`OrdenDelPadron | null`**.
   * No es cosmético, y lo midió el operador fuera del arnés: el Simulador de
   * `R3-UI6` manda `null` (`simulador/selector-participante.tsx:155, :216-217`) con
   * el tipo puente `ConsultaDelPadron & { readonly orden: null }`. Sin el `| null`
   * la intersección da `never` y la compilación de `simulador/**` no cierra. El
   * armado del pedido YA contemplaba las dos ausencias (`:305` —`!== undefined &&
   * !== null`—), así que acá el tipo se alinea con el código y no cambia el
   * comportamiento.
   */
  readonly orden?: OrdenDelPadron | null
}

/**
 * La fila del padrón tal como la pantalla la conoce: los OCHO campos de
 * `ADR-0191` y ni uno más.
 *
 * Los nombres van SIN TRADUCIR, idénticos a `MasterListItem` (`ADR-0191`
 * §Pata ④): un nombre distinto en esta capa sería el lugar donde alguien
 * escribe, en la traducción, una segunda opinión sobre qué es un participante.
 *
 * ⛔ **NO se declara como `MasterListItem` a secas**, y no es cosmético: esa
 * interfaz lleva los cinco campos prohibidos declarados `?: never`, que son una
 * defensa del BORDE DEL SERVIDOR contra reexportar la entidad. Repetirlos acá
 * los volvería propiedades que el código de la pantalla podría nombrar. Lo que
 * esta pantalla conoce son ocho campos, escritos a mano.
 */
export type FilaDelPadron = {
  readonly id: string
  readonly nombre: string
  readonly telefono: string
  readonly lote: string
  readonly sinInteraccion: boolean
  readonly siguioConChatbot: boolean
  readonly pidioHumano: boolean
  readonly optOut: boolean
}

/**
 * Una página del padrón: las filas y el total EN LA BASE del conjunto
 * consultado.
 *
 * ⭐ `total` tiene DOS trabajos y el segundo es el que la pantalla no puede
 * perder: es lo que distingue «no hay nadie con ese filtro» (`items` vacío,
 * `total = 0`) de «pediste una página fuera de rango» (`items` vacío,
 * `total > 0`). Ver `tabla.tsx` `guard` ③.
 */
export type PaginaDelPadron = {
  readonly items: readonly FilaDelPadron[]
  readonly total: number
}

/**
 * El borde del padrón visto desde el navegador.
 *
 * ⛔ **No hay operación de DETALLE, y su ausencia es la mitad de `guard` ② de la
 * etapa.** Una pantalla de detalle es una SEGUNDA puerta al contenido crudo de
 * un menor; y además necesitaría campos que este borde no manda —los cuatro
 * `…FechaHora`, el `arm`—, así que construirla obligaría a ensanchar la
 * proyección de ocho campos por una pantalla en vez de por una decisión.
 *
 * ⛔ **Y no hay operación de ESCRITURA.** El seteo de flags por fila es `AW15b`,
 * y su borde ya existe (`src/borde-participantes.ts`, `PATCH
 * participantes/{id}/flag`): un segundo lugar donde se arma ese pedido sería un
 * segundo `fetch`, un segundo manejo de error y una segunda versión del cuerpo
 * que pueden divergir sin que nada avise.
 */
export type BordePadron = {
  readonly pagina: (consulta: ConsultaDelPadron) => Promise<PaginaDelPadron>
  /** Los lotes del brazo, para el selector de acotamiento. Ver `crearBordePadronHttp`. */
  readonly lotes: () => Promise<readonly string[]>
}

/**
 * El segmento de ruta, **sin ancla de tipo y a sabiendas**.
 *
 * `contracts/read-api` **no tiene `http.ts`** (su directorio es `fake.spec.ts`,
 * `fake.ts`, `index.spec.ts`, `index.ts`) y `ADR-0090` §Lo que este ADR NO
 * decide (d) hereda ese hueco sin cerrarlo, así que `GET padron` es —igual que
 * `GET documentos` y `GET elegibles`— una **invención de `apps/admin-web`**. El
 * precedente de esta app para exactamente este caso es
 * `envios/borde-elegibles.ts` (`RUTA_ELEGIBLES`), y se sigue tal cual: el
 * segmento va como literal sin tipo, y se dice acá que **no lo protege nada**.
 * Si `admin-web` lo renombra, esto compila igual y falla con un `404`.
 *
 * ⚠️ Inventarle un alias local (`type PadronPath = "padron"`) sería un ancla de
 * utilería: se verifica contra sí misma y no puede ponerse roja nunca.
 *
 * ⛔ **No es `participantes`**, y el motivo no es de gusto: esa ruta exacta la
 * atrapa la ingesta en `apps/admin-web/src/rutas.ts`, sin chequeo de método y
 * ARRIBA de `resolverActor` (`AP28` `guard` ②).
 */
const RUTA_PADRON = "padron"

/**
 * Los nombres de los CINCO parámetros, anclados contra los campos del contrato.
 *
 * No es cosmético: son los únicos strings de este archivo que `tsc` puede
 * defender, y defienden justo el caso silencioso — un `pageSize` renombrado no
 * daría `404`, daría un `400` que en pantalla se lee como «no se pudo leer el
 * padrón». `admin-web` los lee con estos mismos nombres, que son los campos de
 * `MasterQuery`, sin traducir (`AP28`).
 */
const PARAMETRO_PAGINA: keyof MasterQuery = "page"
const PARAMETRO_TAMANO: keyof MasterQuery = "pageSize"

/**
 * ⭐ `R3-UI3` · `PRD_R3.md:721` y regla 8 · **«Filas por página» ofrece 10, 50,
 * 100, 200 y 500, con 10 al abrir.**
 *
 * ⛔ La lista se escribe acá, y NO se importa de `../paginado.tsx`
 * (`FILAS_POR_PAGINA`), porque `borde-padron.ts` es el módulo de transporte y no
 * puede importar VALORES de la capa de UI sin invertir la dependencia; y NO se
 * importa de `@contracts/participants` (`PARTICIPANTES_PAGE_SIZES`) porque este
 * proyecto prohíbe importar VALORES de un contrato (rompe el build: ver el
 * encabezado de este archivo). Las tres listas tienen que decir lo mismo: si
 * alguna cambia, las otras dos quedan mintiendo y ninguna suite lo ve.
 */
export const TAMANOS_DE_PAGINA = [10, 50, 100, 200, 500] as const

const PARAMETRO_LOTES: keyof MasterQuery = "lotes"
const PARAMETRO_TELEFONO: keyof MasterQuery = "telefono"
const PARAMETRO_NOMBRE: keyof MasterQuery = "nombre"

/**
 * El parámetro del orden. Se declara `string` y NO `keyof MasterQuery`: lo fija
 * el cable (`cable-R3-UI3.md`), no el tipo del contrato.
 */
const PARAMETRO_ORDEN = "orden"

/** Error del borde con la forma que el resto de la app ya usa (`HttpError`). */
function errorDelBorde(mensaje: string, statusCode: number): Error & { statusCode: number } {
  return Object.assign(new Error(mensaje), { statusCode })
}

function esObjeto(valor: unknown): valor is Record<string, unknown> {
  return typeof valor === "object" && valor !== null && !Array.isArray(valor)
}

/**
 * Arma la query de una consulta del padrón.
 *
 * Exportada y pura para que el examen la afirme directo, sin `fetch` de por
 * medio: es donde viven las dos reglas del cable que ningún tipo hace cumplir.
 *
 * ⛔⭐ **UN CAMPO DE BÚSQUEDA VACÍO SE OMITE; NUNCA SE MANDA PRESENTE Y VACÍO.**
 * `admin-web` rechaza el presente-vacío con `malformedRequest` a propósito
 * (`AP28`), con el argumento de `leerConsultaDeElegibles`: «un filtro sin
 * criterio devuelve todas, y las dos se ven igual desde arriba». Un `?telefono=`
 * vacío tolerado sería **el padrón entero devuelto en respuesta a un campo de
 * búsqueda que el operador borró**. Quien tiene que no mandarlo es esta
 * pantalla, y lo hace acá, una sola vez.
 *
 * ⛔ **`lotes` va REPETIDO —`?lotes=A&lotes=B`— y NUNCA separado por comas.** Un
 * `nombre_db` es un string opaco que puede tener una coma adentro, así que
 * juntarlos con `,` parte un lote en dos y ninguno de los dos existe.
 */
export function construirQueryDelPadron(consulta: ConsultaDelPadron): URLSearchParams {
  const parametros = new URLSearchParams()

  parametros.set(PARAMETRO_PAGINA, String(consulta.pagina))
  parametros.set(PARAMETRO_TAMANO, String(consulta.tamanoDePagina))

  for (const lote of consulta.filtro.lotes) {
    parametros.append(PARAMETRO_LOTES, lote)
  }

  if (consulta.filtro.telefono !== null) {
    parametros.set(PARAMETRO_TELEFONO, consulta.filtro.telefono)
  }
  if (consulta.filtro.nombre !== null) {
    parametros.set(PARAMETRO_NOMBRE, consulta.filtro.nombre)
  }

  /*
   * ⛔⭐ EL ORDEN VIAJA COMO `<columna>-<sentido>` Y SÓLO SI SE PIDIÓ. Ausente ⇒
   * rige el default del servidor; mandarlo vacío o inventar un default acá sería
   * una segunda verdad sobre cómo se ordena.
   *
   * ⛔⭐ **`null` ES TAN AUSENTE COMO `undefined`, Y NO ES COSMÉTICO.**
   * `pruebas/r11-brazo-en-la-ruta.test.ts:30-35` construye la consulta con
   * `orden: null` para medir el camino del pedido; con un guard que sólo mirara
   * `!== undefined`, `null.columna` reventaba ANTES de tocar la red, el pedido no
   * salía y dos casos de esa suite se ponían rojos por un `TypeError`, no por la
   * URL. La pantalla (`tabla.tsx:993`) ya omite la clave cuando su estado es
   * `null`; tolerarlo también acá cierra el otro llamador.
   */
  if (consulta.orden !== undefined && consulta.orden !== null) {
    parametros.set(PARAMETRO_ORDEN, `${consulta.orden.columna}-${consulta.orden.sentido}`)
  }

  return parametros
}

/**
 * Valida una fila del cable y la PROYECTA a los OCHO campos de la pantalla.
 *
 * El paso por `MasterListItem` es lo que hace que el renombre de un campo del
 * contrato sea un error de compilación acá y no una columna vacía en pantalla.
 *
 * ⚠️ **`lote` puede llegar como cadena VACÍA y eso es un estado legítimo, no un
 * dato faltante**: `modules/participants` usa `""` para el alta manual —«un alta
 * manual no proviene de ningún batch, y la cadena vacía es la única forma de
 * decirlo que no inventa un nombre de lote que ninguna fuente fija»— y
 * `ADR-0181` midió el precio de ignorarlo. Por eso la validación pide `string` y
 * **no pide que no esté vacío**: rechazar `""` acá tiraría la fila entera. Cómo
 * se ROTULA esa celda lo decide `tabla.tsx` (`ADR-0191` §NO decide, fila **e**).
 */
function leerFila(valor: unknown): FilaDelPadron | null {
  if (!esObjeto(valor)) return null

  const id = valor["id"]
  const nombre = valor["nombre"]
  const telefono = valor["telefono"]
  const lote = valor["lote"]
  const sinInteraccion = valor["sinInteraccion"]
  const siguioConChatbot = valor["siguioConChatbot"]
  const pidioHumano = valor["pidioHumano"]
  const optOut = valor["optOut"]

  if (typeof id !== "string") return null
  if (typeof nombre !== "string") return null
  if (typeof telefono !== "string") return null
  if (typeof lote !== "string") return null
  if (typeof sinInteraccion !== "boolean") return null
  if (typeof siguioConChatbot !== "boolean") return null
  if (typeof pidioHumano !== "boolean") return null
  if (typeof optOut !== "boolean") return null

  /*
   * ⭐ EL OBJETO LITERAL, OCHO PROPIEDADES ESCRITAS A MANO. Ver ② arriba.
   *
   * Tipado contra `MasterListItem` para que el día que el contrato renombre un
   * campo, este build se caiga en vez de dibujar una columna vacía.
   */
  const fila: MasterListItem = {
    id,
    nombre,
    telefono,
    lote,
    sinInteraccion,
    siguioConChatbot,
    pidioHumano,
    optOut,
  }

  /* Y la salida, otra vez campo por campo: lo que sale de acá tiene ocho claves. */
  return {
    id: fila.id,
    nombre: fila.nombre,
    telefono: fila.telefono,
    lote: fila.lote,
    sinInteraccion: fila.sinInteraccion,
    siguioConChatbot: fila.siguioConChatbot,
    pidioHumano: fila.pidioHumano,
    optOut: fila.optOut,
  }
}

/**
 * Valida la página: las filas más el total EN LA BASE del conjunto consultado.
 *
 * ⛔ `total` sale del cable SIEMPRE, nunca de `items.length`: `items.length` es
 * cuántas filas entraron en esta página, y con él el paginado mentiría **y los
 * dos vacíos se volverían indistinguibles**, que es justo lo que `total` existe
 * para separar.
 */
function leerPagina(valor: unknown): PaginaDelPadron | null {
  if (!esObjeto(valor)) return null

  const itemsCrudo = valor["items"]
  if (!Array.isArray(itemsCrudo)) return null

  const filas: FilaDelPadron[] = []
  for (const item of itemsCrudo) {
    const fila = leerFila(item)
    if (fila === null) return null
    filas.push(fila)
  }

  const total = valor["total"]
  if (typeof total !== "number" || !Number.isInteger(total) || total < 0) return null

  // El nombre del campo del cable sale del contrato, no de esta capa.
  const anclaDelTotal: MasterResult["total"] = total
  return { items: filas, total: anclaDelTotal }
}

/**
 * Arma el borde del padrón contra la `admin-web` configurada.
 *
 * Función y no constante por lo mismo que los otros bordes: la dirección llega
 * en tiempo de ejecución (`configuracion.json`) y no hay ningún momento de
 * import en el que conocerla. `App.tsx` la memoiza con `useMemo` sobre `config`,
 * porque el borde es dependencia de un `useEffect` y uno nuevo por render sería
 * un bucle de consultas contra el padrón.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⚠️ `lotesDelBrazo` ENTRA INYECTADO, Y ES UN HUECO DECLARADO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * El selector de acotamiento necesita la lista de lotes del brazo. **`AP28` no
 * agrega ninguna ruta que la sirva**, y la única que existe hoy en `admin-web`
 * es `GET exportaciones/lotes`, que `src/exportacion/borde-exportacion.ts` ya
 * consume. Del otro lado del cable las dos son **la misma fuente**:
 * `contracts/exports-api` importa el listado del selector de
 * `contracts/participants` —`LotesDisponibles = Pick<ParticipantsRead, "lotesDe">`,
 * ⟦la-lectura-por-lotes-ya-tiene-donde-salir⟧— justamente para que no haya «una
 * segunda declaración» de qué lotes tiene un brazo.
 *
 * ⇒ **este archivo no repite ese `fetch` ni ese literal de ruta.** Recibe la
 * función que ya existe y la expone. Un segundo `fetch` acá sería un segundo
 * lugar donde se escribe `exportaciones/lotes`: el día que esa ruta cambie, uno
 * se caería y el otro seguiría hablándole a un endpoint que no existe, con
 * `tsc` en cero — que es exactamente el modo de falla que `borde-participantes.ts`
 * ya dejó escrito para las rutas sin ancla de tipo.
 *
 * ⚠️ **Que el padrón tenga que pedirle su selector a la ruta de EXPORTACIÓN es
 * un hueco de la cadena, no una decisión de esta pantalla**, y va reportado: el
 * dueño de darle ruta propia al listado de lotes es `apps-integrator`.
 */
export function crearBordePadronHttp(
  config: ConfiguracionInstancia,
  lotesDelBrazo: () => Promise<readonly string[]>,
): BordePadron {
  /**
   * ⭐ `AR5-m` · ⟦el-brazo-entra-por-ruta⟧ · **la raíz lleva el brazo adelante**, y
   * es una FUNCIÓN y no una constante porque el brazo sale de la dirección, que
   * cambia sin que este borde se vuelva a construir (`App.tsx` lo memoiza por
   * `config`). Ver `brazo-del-cable.ts`, que tiene la decisión del dueño citada.
   */
  const raiz = (): string => raizDelBrazo(config.adminWebUrl)

  return {
    pagina: async (consulta: ConsultaDelPadron): Promise<PaginaDelPadron> => {
      const parametros = construirQueryDelPadron(consulta)

      let respuesta: Response
      try {
        /*
         * `credentials: "include"` y ningún `Authorization` armado acá, igual
         * que los otros bordes: la sesión es una cookie `HttpOnly` que el
         * navegador manda sola. Un token que el JavaScript tuviera que leer
         * sería un token que esta pantalla tuvo que GUARDAR, y esta unidad no
         * guarda nada, ni credenciales ni estado.
         */
        respuesta = await fetch(`${raiz()}/${RUTA_PADRON}?${parametros.toString()}`, {
          method: "GET",
          headers: { Accept: "application/json" },
          credentials: "include",
        })
      } catch (causa) {
        throw Object.assign(errorDelBorde(`No se pudo hablar con el servidor (${config.adminWebUrl}).`, 0), {
          cause: causa,
        })
      }

      if (!respuesta.ok) {
        throw errorDelBorde(
          await mensajeDeError(
            respuesta,
            `El servidor respondió ${respuesta.status} al listar el padrón.`,
          ),
          respuesta.status,
        )
      }

      const pagina = leerPagina(await respuesta.json())
      if (pagina === null) {
        throw errorDelBorde(
          "El servidor contestó el padrón en una forma que no se entiende.",
          502,
        )
      }
      return pagina
    },

    lotes: lotesDelBrazo,
  }
}
